import React, { useCallback, useEffect, useState } from "react";
import { useAuth } from "@/auth";
import { useI18n } from "@/i18n";
import { toast } from "sonner";
import { Plus, X, Trash2, Building2 } from "lucide-react";
import { fmtMoney } from "@/lib/money";

const today = () => new Date().toISOString().slice(0, 10);
const EMPTY = { oficina: "", fecha: today(), tipo: "cargo", concepto: "", monto: 0, notas: "" };

export const BranchesTab = ({ isGlobal, oficinas = [] }) => {
  const { api } = useAuth();
  const { t } = useI18n();
  const [data, setData] = useState(null);
  const [entries, setEntries] = useState([]);
  const [modal, setModal] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    const [b, l] = await Promise.all([api.get("/accounting/branch-balances"), api.get("/branch-ledger")]);
    setData(b.data); setEntries(l.data || []);
  }, [api]);
  useEffect(() => { load().catch(() => {}); }, [load]);

  const cur = data?.moneda || "EUR";
  const money = (v) => fmtMoney(v, cur);

  const submit = async (e) => {
    e.preventDefault(); setBusy(true);
    try {
      await api.post("/branch-ledger", form);
      toast.success(t("saved")); setModal(false); setForm(EMPTY); load();
    } catch (err) { toast.error(err.response?.data?.detail || "Error"); }
    finally { setBusy(false); }
  };
  const del = async (id) => {
    if (!window.confirm(t("confirm_delete"))) return;
    try { await api.delete(`/branch-ledger/${id}`); load(); }
    catch (err) { toast.error(err.response?.data?.detail || "Error"); }
  };

  const inp = "w-full px-3 py-2 border border-slate-300 rounded-lg text-sm outline-none focus:border-indigo-600";
  const lbl = "block text-[11px] font-semibold text-slate-600 mb-1 uppercase tracking-wide";

  return (
    <div className="space-y-4" data-testid="acc-branches">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div className="rounded-xl border border-indigo-200 bg-indigo-50 px-4 py-2.5">
          <span className="text-[11px] font-bold uppercase tracking-wider text-indigo-700">{t("owed_by_branches")}</span>
          <span className="ms-3 text-xl font-semibold tabular-nums text-indigo-900" data-testid="branches-total-debt">{money(data?.total_deuda || 0)}</span>
        </div>
        {isGlobal && (
          <button data-testid="new-ledger-btn" onClick={() => { setForm(EMPTY); setModal(true); }} className="bg-indigo-600 text-white px-3 py-2 rounded-lg font-semibold text-xs flex items-center gap-1.5">
            <Plus className="w-3.5 h-3.5" /> {t("new_ledger_entry")}
          </button>
        )}
      </div>

      <div className="bg-white border border-slate-200 rounded-xl overflow-x-auto">
        <div className="px-4 py-3 border-b border-slate-100 text-xs font-bold uppercase tracking-wider text-slate-500 flex items-center gap-2"><Building2 className="w-3.5 h-3.5" /> {t("branch_account")}</div>
        <table className="w-full text-sm min-w-[700px]">
          <thead className="bg-slate-50 text-[11px] uppercase text-slate-500">
            <tr>
              <th className="p-3 text-start">{t("office")}</th>
              <th className="p-3 text-end">{t("charges")}</th>
              <th className="p-3 text-end">{t("credits")}</th>
              <th className="p-3 text-end">{t("balance")}</th>
              <th className="p-3 text-end">{t("invoiced")}</th>
              <th className="p-3 text-end">{t("collected")}</th>
              <th className="p-3 text-end">{t("total_expenses")}</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {(data?.rows || []).length === 0 && <tr><td colSpan={7} className="p-8 text-center text-slate-400">—</td></tr>}
            {(data?.rows || []).map(r => (
              <tr key={r.oficina} data-testid={`branch-row-${r.oficina}`}>
                <td className="p-3 font-medium">{r.oficina}</td>
                <td className="p-3 text-end tabular-nums text-red-700">{money(r.cargos)}</td>
                <td className="p-3 text-end tabular-nums text-emerald-700">{money(r.abonos)}</td>
                <td className={`p-3 text-end tabular-nums font-semibold ${r.saldo > 0 ? "text-indigo-900" : "text-slate-500"}`}>{money(r.saldo)}</td>
                <td className="p-3 text-end tabular-nums text-slate-600">{money(r.facturado)}</td>
                <td className="p-3 text-end tabular-nums text-slate-600">{money(r.cobrado)}</td>
                <td className="p-3 text-end tabular-nums text-slate-600">{money(r.gastos)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="bg-white border border-slate-200 rounded-xl overflow-x-auto">
        <div className="px-4 py-3 border-b border-slate-100 text-xs font-bold uppercase tracking-wider text-slate-500">{t("ledger_entries")}</div>
        <table className="w-full text-sm min-w-[640px]">
          <thead className="bg-slate-50 text-[11px] uppercase text-slate-500">
            <tr><th className="p-3 text-start">{t("date")}</th><th className="p-3 text-start">{t("office")}</th><th className="p-3 text-start">{t("movement")}</th><th className="p-3 text-start">{t("concept")}</th><th className="p-3 text-end">{t("amount")}</th><th className="p-3 text-start">{t("who")}</th><th className="p-3"></th></tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {entries.length === 0 && <tr><td colSpan={7} className="p-8 text-center text-slate-400">—</td></tr>}
            {entries.map(e => (
              <tr key={e.id} data-testid={`ledger-row-${e.id}`}>
                <td className="p-3 tabular-nums text-slate-600">{e.fecha}</td>
                <td className="p-3">{e.oficina}</td>
                <td className="p-3"><span className={`text-[10px] font-bold uppercase px-2 py-0.5 rounded-full ${e.tipo === "cargo" ? "bg-red-100 text-red-700" : "bg-emerald-100 text-emerald-700"}`}>{t(`mov_${e.tipo}`)}</span></td>
                <td className="p-3">{e.concepto || "—"}</td>
                <td className="p-3 text-end tabular-nums font-semibold">{money(e.monto)}</td>
                <td className="p-3 text-xs text-slate-500">{e.created_by_name || "—"}</td>
                <td className="p-3 text-end">{isGlobal && <button data-testid={`ledger-del-${e.id}`} onClick={() => del(e.id)} className="p-1.5 text-slate-400 hover:text-red-600 rounded"><Trash2 className="w-4 h-4" /></button>}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {modal && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4 overflow-y-auto">
          <form onSubmit={submit} className="bg-white rounded-xl w-full max-w-md p-6 space-y-3 my-8" data-testid="ledger-modal">
            <div className="flex items-center justify-between"><h3 className="font-display font-semibold text-lg">{t("new_ledger_entry")}</h3><button type="button" onClick={() => setModal(false)}><X className="w-5 h-5 text-slate-400" /></button></div>
            <p className="text-[11px] text-slate-500">{t("ledger_hint")}</p>
            <div>
              <label className={lbl}>{t("office")}</label>
              <select required data-testid="ledger-oficina" value={form.oficina} onChange={(e) => setForm({ ...form, oficina: e.target.value })} className={inp}>
                <option value="">—</option>
                {oficinas.map(o => <option key={o} value={o}>{o}</option>)}
              </select>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className={lbl}>{t("movement")}</label>
                <select data-testid="ledger-tipo" value={form.tipo} onChange={(e) => setForm({ ...form, tipo: e.target.value })} className={inp}>
                  <option value="cargo">{t("mov_cargo")}</option>
                  <option value="abono">{t("mov_abono")}</option>
                </select>
              </div>
              <div><label className={lbl}>{t("date")}</label><input required type="date" value={form.fecha} onChange={(e) => setForm({ ...form, fecha: e.target.value })} className={inp} /></div>
            </div>
            <div><label className={lbl}>{t("concept")}</label><input required data-testid="ledger-concepto" value={form.concepto} onChange={(e) => setForm({ ...form, concepto: e.target.value })} className={inp} /></div>
            <div><label className={lbl}>{t("amount")}</label><input required type="number" step="0.01" data-testid="ledger-monto" value={form.monto} onChange={(e) => setForm({ ...form, monto: +e.target.value })} className={inp} /></div>
            <div className="flex gap-3 pt-1"><button type="button" onClick={() => setModal(false)} className="flex-1 border border-slate-300 py-2.5 rounded-lg font-semibold text-sm">{t("cancel")}</button><button type="submit" disabled={busy} data-testid="ledger-submit" className="flex-1 bg-indigo-600 disabled:opacity-50 text-white py-2.5 rounded-lg font-semibold text-sm">{t("save")}</button></div>
          </form>
        </div>
      )}
    </div>
  );
};
